import React, { useState } from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
} from '@mui/material';
import { ILegalTransactionFullObject } from '../Interfaces/ILegalTransaction';
import { ITaskFullObject, taskFullObjectSchema } from '../Interfaces/ITask';
import { ILegalClient } from '../Interfaces/ILegalClient';
import { IUser } from '../Interfaces/IUser';
import { IUserGroup } from '../Interfaces/IUserGroup';

import {
  Check,
  Delete,
  Edit,
  Group,
  Person,
  RestartAlt,
  Warning,
} from '@mui/icons-material';
import { TaskPriorityChip } from './TaskPriorityChip';
import { TaskDeadlineChip } from './TaskDeadlineChip';
import { EditTask } from './EditTask';
import { ITaskType } from '../Interfaces/ITaskType';
import { ITaskPriority } from '../Interfaces/ITaskPriority';
import { useGetLegalTransactions } from '../hooks/reactQuerys/LegalTransactionQueries';

interface IProps {
  task: ITaskFullObject;
  tasks: ITaskFullObject[];
  setTasks: Function;
  legalClients?: ILegalClient[];
  users: IUser[];
  userGroups: IUserGroup[];
  taskTypes: ITaskType[];
  taskPriorities: ITaskPriority[];
  legalTransaction?: ILegalTransactionFullObject;
  legalClient?: ILegalClient;
}

export const TaskRow: React.FC<IProps> = ({
  task,
  tasks,
  setTasks,
  legalClients,
  users,
  userGroups,
  taskTypes,
  taskPriorities,
  legalTransaction,
  legalClient,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [orgTask, setOrgTask] = useState(task);
  const [editTask, setEditTask] = useState(
    taskFullObjectSchema.parse({
      idLegalTransaction: legalTransaction
        ? legalTransaction.idLegalTransaction
        : null,
      idLegalClient: legalClient ? legalClient.idLegalClient : null,
    })
  );

  const {
    legalTransactions,
    isLoadingLegalTransactions,
    refetchLegalTransactions,
  } = useGetLegalTransactions(legalTransaction === undefined);

  const handleOpen = (task: ITaskFullObject) => {
    setEditTask({ ...task });
    setIsOpen(true);
  };
  const handleClose = () => {
    setIsOpen(false);
  };
  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    setTasks([
      ...tasks.map(t => (t.idTask === editTask.idTask ? editTask : t)),
    ]);

    handleClose();
  };
  const handleRestore = () => {
    const updatedObject: ITaskFullObject = {
      ...task,
      Closed_at: null,
    };
    setEditTask(updatedObject);
    setTasks([
      ...tasks.map(t => (t.idTask === editTask.idTask ? updatedObject : t)),
    ]);
  };
  const handleRemove = () => {
    setTasks([...tasks.filter(t => t.idTask !== task.idTask)]);
  };

  return (
    <>
      {isOpen && (
        <Dialog open onClose={handleClose} maxWidth='md' fullWidth>
          <form onSubmit={handleSave}>
            <DialogContent>
              <EditTask
                editTask={editTask}
                setEditTask={setEditTask}
                taskPriority={taskPriorities}
                taskTypes={taskTypes}
                users={users}
                userGroups={userGroups}
                legalClients={legalClients}
                legalTransactionFullObj={legalTransaction}
                legalClient={legalClient}
                disableEdit={task.Closed_at !== null}
              />
            </DialogContent>
            <DialogActions>
              <Button variant='outlined' onClick={handleClose}>
                Abbruch
              </Button>
              <Button variant='contained' type='submit'>
                Übernehmen
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      )}

      <TableRow
        key={`task-${task.idTask}`}
        sx={{
          '& .MuiTableCell-root': {
            color:
              task.Closed_at != null
                ? 'inherit'
                : new Date(task.Deadline_at) < new Date()
                  ? 'error.main'
                  : task.Reminder_at && new Date(task.Reminder_at) <= new Date()
                    ? 'warning.main'
                    : 'inherit',
          },
        }}
      >
        <TableCell>
          {task.Closed_at ? (
            <Tooltip
              title={`Aufgabe wurde am ${new Date(task.Closed_at).toLocaleDateString()} erledigt.`}
            >
              <Check />
            </Tooltip>
          ) : task.Deadline_at && new Date(task.Deadline_at) < new Date() ? (
            <Tooltip title='Aufgabe ist fällig!'>
              <Warning />
            </Tooltip>
          ) : task.Reminder_at && new Date(task.Reminder_at) <= new Date() ? (
            <Tooltip title='Erinnert!'>
              <Warning />
            </Tooltip>
          ) : null}
        </TableCell>
        <TableCell>
          {
            taskTypes.find(taskType => taskType.idTaskType === task.idTaskType)
              ?.TaskType
          }
        </TableCell>
        <TableCell>
          <TaskPriorityChip task={task} taskPriorities={taskPriorities} />{' '}
        </TableCell>
        <TableCell>
          <TaskDeadlineChip task={task} />
        </TableCell>
        <TableCell>
          {users
            .filter(user => task.Users.includes(user.idUser))
            .map(user => (
              <>
                <Person fontSize='small' />{' '}
                {`${user.FirstName} ${user.LastName}`}
                <br />
              </>
            ))}
          {userGroups
            .filter(userGrp => task.UserGroups.includes(userGrp.idUserGroup))
            .map(userGrp => (
              <>
                <Group fontSize='small' /> {`${userGrp.UserGroup}`}
                <br />
              </>
            ))}
        </TableCell>
        <TableCell>{task.Title}</TableCell>
        <TableCell>
          {orgTask.Closed_at === null ? (
            <>
              <IconButton
                key={`edit-task-${task.idTask}`}
                size='small'
                onClick={() => handleOpen(task)}
              >
                <Edit />
              </IconButton>
              <IconButton size='small' onClick={handleRemove}>
                <Delete />
              </IconButton>
            </>
          ) : (
            new Date(orgTask.Deadline_at) >= new Date() && (
              <IconButton
                key={`edit-task-${task.idTask}`}
                size='small'
                onClick={handleRestore}
                title='Als offen markieren'
              >
                <RestartAlt />
              </IconButton>
            )
          )}
        </TableCell>
      </TableRow>
    </>
  );
};
